import React, { useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Grid,
  Button as MuiButton,
} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import { type AxiosInstance } from "axios";
import { mutate } from "swr";
import { formatLatLng } from "@/utils/utils";
import {
  type SavedMapPlotRequestDetail,
  type AdditionalFieldsFormData,
} from "../types";
import type L from "leaflet";
import { latLng } from "leaflet";
import {
  convertStringToAdditionalFields,
  convertFormDataToAdditionalFields,
} from "../utils/additionalFieldsHelpers";
import type { AdditionalFieldConfig } from "@/features/accountSettings/types/additionalFields";
import { SavedPlotMemoModal } from "./SavedPlotMemoModal";
import { OtherButtonModal } from "./OtherButtonModal";
import { getGoogleMapsUrl } from "../utils/openGoogleMaps";
import { getChikaMapUrl } from "../utils/openChikaMap";
import { toast } from "react-toastify";
import { AddressIntegrator } from "@/utils/AddressIntegrator";
import type { ChibanInput } from "@/types/kaokuNumber";
import { type RealEstateRequestModalHandler } from "../types/realEstateRequest";

interface SavedPlotPopupContentProps {
  plot: SavedMapPlotRequestDetail;
  apiClient: AxiosInstance;
  map: L.Map;
  plotRequestId: number | undefined;
  onRemarksSaved?: (plotId: number, newRemarks: string) => void;
  onTagsSaved?: (plotId: number, newTags: string[]) => void;
  onAdditionalFieldsSaved?: (
    plotId: number,
    newAdditionalFields: string
  ) => void;
  availableFields?: AdditionalFieldConfig;
  crm?: boolean;
  // 地番検索
  kaokuNumberSearchFlag?: boolean;
  handleKaokuNumberSearch?: (chibanList: ChibanInput[]) => void;
  // 役所資料取得依頼
  realEstateDocumentFetchFlag?: boolean;
  handleRealEstateDocumentFetchModal?: RealEstateRequestModalHandler;
  handleRealEstateResearchRequestModal?: RealEstateRequestModalHandler;
  // 物件概要書
  propertySummary?: boolean;
  handleNewPropertySheet?: (adi: AddressIntegrator) => void;
  // 地価マップ有効、無効の判定フラグ
  priceMap?: boolean;
}

export const SavedPlotPopupContent: React.FC<SavedPlotPopupContentProps> = ({
  plot,
  apiClient,
  map,
  plotRequestId,
  onRemarksSaved,
  onTagsSaved,
  onAdditionalFieldsSaved,
  availableFields,
  crm = false,
  // 地番検索
  kaokuNumberSearchFlag = false,
  handleKaokuNumberSearch,
  // 役所資料取得依頼
  realEstateDocumentFetchFlag = false,
  handleRealEstateDocumentFetchModal,
  handleRealEstateResearchRequestModal,
  // 物件概要書
  propertySummary = false,
  handleNewPropertySheet,
  priceMap = false,
}) => {
  // メモモーダル（タグ・備考・カスタム項目編集）の表示状態
  const [memoModalOpen, setMemoModalOpen] = useState(false);

  // その他操作モーダルの表示状態
  const [otherButtonModalOpen, setOtherButtonModalOpen] = useState(false);

  // タグ・備考・カスタム項目の現在値を管理（モーダルに渡すため）
  const [currentTags, setCurrentTags] = useState<string[]>(
    plot.tags?.map((t) => t.name) ?? []
  );
  const [currentRemarks, setCurrentRemarks] = useState(plot.remarks ?? "");
  const [currentAdditionalFields, setCurrentAdditionalFields] =
    useState<AdditionalFieldsFormData>(
      convertStringToAdditionalFields(plot.additionalFields, availableFields)
    );
  const effectivePlotRequestId = plot.mapPlotRequestId ?? plotRequestId;

  // プロット詳細を更新する共通関数
  const updatePlotDetail = async (
    update:
      | { remarks: string }
      | { tags: string[] }
      | { additionalFields: Record<string, { value: string }> | null }
  ): Promise<void> => {
    const payload = {
      details: [
        {
          id: plot.id,
          ...update,
        },
      ],
    };

    await apiClient.patch("/map/plot-request-details", payload);

    // プロット詳細のキャッシュを無効化して再取得
    if (effectivePlotRequestId !== undefined) {
      void mutate(`/map/plot-request/${effectivePlotRequestId}`);
    }
  };

  const handleSaveRemarks = async (newRemarks: string): Promise<void> => {
    try {
      await updatePlotDetail({ remarks: newRemarks });
      onRemarksSaved?.(plot.id, newRemarks);
      setCurrentRemarks(newRemarks);
      toast.success("備考を保存しました");
    } catch (error) {
      toast.error("備考の保存に失敗しました");
      throw error;
    }
  };

  const handleSaveTags = async (newTags: string[]): Promise<void> => {
    try {
      await updatePlotDetail({ tags: newTags });
      onTagsSaved?.(plot.id, newTags);
      setCurrentTags(newTags);
      toast.success("タグを保存しました");
    } catch (error) {
      toast.error("タグの保存に失敗しました");
      throw error;
    }
  };

  const handleSaveAdditionalFields = async (
    data: AdditionalFieldsFormData
  ): Promise<void> => {
    try {
      if (!availableFields) {
        toast.error("カスタム項目の設定が取得できません");
        return;
      }

      // フォームデータをAPI用の形式に変換
      const convertedData = convertFormDataToAdditionalFields(
        data,
        availableFields
      );

      // 空のオブジェクトの場合はnullを送信
      const fieldsToUpdate =
        Object.keys(convertedData).length > 0 ? convertedData : null;

      await updatePlotDetail({ additionalFields: fieldsToUpdate });

      // SavedPlotLayerのadditionalFieldsMapRef(Map<number, string>)に保存するため、
      // JSON文字列化してコールバックに渡す。ポップアップ再表示時に使用される。
      const stringifiedData = JSON.stringify(convertedData);
      onAdditionalFieldsSaved?.(plot.id, stringifiedData);

      // ローカル状態を更新
      setCurrentAdditionalFields(data);

      toast.success("カスタム項目を保存しました");
    } catch (error) {
      toast.error("カスタム項目の保存に失敗しました");
      throw error;
    }
  };

  // 緯度経度の表示形式を整える
  const formatCoordinates = (lat: number, lng: number): string => {
    return `${formatLatLng(lat)}, ${formatLatLng(lng)}`;
  };

  // 住居表示
  const displayAddress = plot.requestAddress ?? null;

  // 座標
  const displayCoordinates =
    plot.substituteLatitude != null && plot.substituteLongitude != null
      ? formatCoordinates(plot.substituteLatitude, plot.substituteLongitude)
      : null;

  // NOTE: realEstateTypeがundefinedの場合はデフォルトで土地として扱う
  const bookType = plot.realEstateType ?? "土地";
  const isBuilding = bookType === "建物" || bookType === "区分建物";
  const chibanLabel = isBuilding ? "家屋番号" : "地番";

  // 地番または家屋番号
  const chibanOrKaokuNumber: string | null = isBuilding
    ? plot.requestKaokuNumber ?? null
    : plot.requestChiban;

  const displayLocation = plot.requestLocation ?? "";
  const displayChibanNumber = chibanOrKaokuNumber ?? "";

  // Google Maps（衛星写真）のURL
  const googleMapsUrl =
    plot.substituteLatitude != null && plot.substituteLongitude != null
      ? getGoogleMapsUrl(map, {
          lat: plot.substituteLatitude,
          lng: plot.substituteLongitude,
        })
      : undefined;

  // 地価マップのURL
  const chikaMapUrl =
    plot.substituteLatitude != null && plot.substituteLongitude != null
      ? getChikaMapUrl({
          lat: plot.substituteLatitude,
          lng: plot.substituteLongitude,
        })
      : undefined;

  // 都道府県名・所在・地番/家屋番号が全て揃っているか
  const hasLocationAndChibanOrKaokuNumber: boolean =
    (plot.prefName ?? "") !== "" &&
    displayLocation !== "" &&
    (chibanOrKaokuNumber ?? "") !== "";

  // 物件概要書ボタンの有効条件（住居表示・所在・地番/家屋番号・座標が全て必要）
  const canShowPropertySummaryButton: boolean =
    hasLocationAndChibanOrKaokuNumber &&
    (plot.requestAddress ?? "") !== "" &&
    plot.substituteLatitude != null &&
    plot.substituteLongitude != null;

  // 地番検索ハンドラー
  const handleClickKaokuNumberSearchButton =
    handleKaokuNumberSearch &&
    kaokuNumberSearchFlag &&
    hasLocationAndChibanOrKaokuNumber
      ? () => {
          const chibanInput: ChibanInput = {
            prefectureName: plot.prefName ?? "",
            location: displayLocation,
            chiban: chibanOrKaokuNumber ?? "",
            bookType: isBuilding ? "ONE_BUILDING" : "LAND",
            latitude: plot.substituteLatitude,
            longitude: plot.substituteLongitude,
          };
          handleKaokuNumberSearch([chibanInput]);
        }
      : undefined;

  // 役所資料取得依頼ハンドラー
  const handleRealEstateDocumentFetch =
    handleRealEstateDocumentFetchModal && realEstateDocumentFetchFlag
      ? () => {
          handleRealEstateDocumentFetchModal(
            plot.prefName ?? "",
            plot.requestAddress ?? "",
            chibanOrKaokuNumber ?? "",
            displayLocation,
            true,
            isBuilding ? "ONE_BUILDING" : "LAND"
          );
        }
      : undefined;

  const handleRealEstateResearchRequest = handleRealEstateResearchRequestModal
    ? () => {
        handleRealEstateResearchRequestModal(
          plot.prefName ?? "",
          plot.requestAddress ?? "",
          chibanOrKaokuNumber ?? "",
          displayLocation,
          true,
          isBuilding ? "ONE_BUILDING" : "LAND"
        );
      }
    : undefined;

  // 物件概要書作成ハンドラー
  const handleCreatePropertySheet =
    handleNewPropertySheet && propertySummary && canShowPropertySummaryButton
      ? () => {
          const adi = new AddressIntegrator({
            prefName: plot.prefName ?? "",
            cityName: "", // 市区町村名は分けられていない。(displayChibanに含まれる)
            ooazaName: displayLocation, // 所在をooazaNameとして設定（buildLocationFull()で利用される）
            address: plot.requestAddress ?? "",
            addressChiban: `${displayLocation}${chibanOrKaokuNumber ?? ""}`,
            chiban: chibanOrKaokuNumber ?? "",
            latlng: latLng(plot.substituteLatitude!, plot.substituteLongitude!),
            area: null, // 面積は保存済み物件データに含まれていないため null
          });
          handleNewPropertySheet(adi);
        }
      : undefined;

  const handleOpenFilesPage =
    effectivePlotRequestId !== undefined
      ? () => {
          window.open(
            `/mapplotrequest/${effectivePlotRequestId}/details/${plot.id}/files`,
            "_blank",
            "noopener,noreferrer"
          );
        }
      : undefined;

  // 地図コンテナの横幅
  const mapContainerSizeX = map.getSize().x;

  // ポップアップのデフォルトサイズ(px)
  let popupWidth = 300;

  // 地図コンテナ幅375px以下では（300px幅のポップアップ＋余白）がきれいに収まらないので、ポップアップ幅を縮小する
  if (mapContainerSizeX < 375) {
    popupWidth = Math.floor(mapContainerSizeX * 0.8);
  }

  return (
    <>
      <Box sx={{ width: popupWidth }}>
        <FormControl sx={{ width: "100%" }}>
          {displayAddress && (
            <>
              <FormLabel>
                <PlaceIcon fontSize={"inherit"} />
                住居表示
              </FormLabel>
              <Typography
                ml={2}
                my={1}
                fontSize={"0.8rem"}
                variant={"body1"}
                component={"span"}
                sx={{ wordBreak: "break-word" }}
              >
                {displayAddress}
              </Typography>
            </>
          )}
          <FormLabel>
            <ImportContactsIcon fontSize={"inherit"} />
            種別
          </FormLabel>
          <Typography
            ml={2}
            my={1}
            fontSize={"0.8rem"}
            variant={"body1"}
            component={"span"}
            sx={{ wordBreak: "break-word" }}
          >
            {bookType}
          </Typography>
          {displayLocation && (
            <>
              <FormLabel>
                <ImportContactsIcon fontSize={"inherit"} />
                所在
              </FormLabel>
              <Typography
                ml={2}
                my={1}
                fontSize={"0.8rem"}
                variant={"body1"}
                component={"span"}
                sx={{ wordBreak: "break-word" }}
              >
                {displayLocation}
              </Typography>
            </>
          )}
          {displayChibanNumber && (
            <>
              <FormLabel>
                <ImportContactsIcon fontSize={"inherit"} />
                {chibanLabel}
              </FormLabel>
              <Typography
                ml={2}
                my={1}
                fontSize={"0.8rem"}
                variant={"body1"}
                component={"span"}
                sx={{ wordBreak: "break-word" }}
              >
                {displayChibanNumber}
              </Typography>
            </>
          )}
          {displayCoordinates && (
            <>
              <FormLabel>
                <MyLocationIcon fontSize={"inherit"} />
                座標
              </FormLabel>
              <Typography
                ml={2}
                my={1}
                fontSize={"0.8rem"}
                variant={"body1"}
                component={"span"}
                sx={{ wordBreak: "break-word" }}
              >
                {displayCoordinates}
              </Typography>
            </>
          )}
        </FormControl>
        {crm && (
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <MuiButton
                  onClick={() => {
                    setOtherButtonModalOpen(true);
                  }}
                  sx={{ textTransform: "none" }}
                  variant="outlined"
                  color="primary"
                  fullWidth
                >
                  その他操作
                </MuiButton>
              </Grid>
              <Grid item xs={6}>
                <MuiButton
                  onClick={() => {
                    setMemoModalOpen(true);
                  }}
                  startIcon={<NoteAltIcon />}
                  sx={{ textTransform: "none" }}
                  variant="outlined"
                  color="primary"
                  fullWidth
                >
                  メモ
                </MuiButton>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
      <SavedPlotMemoModal
        apiClient={apiClient}
        open={memoModalOpen}
        detailId={plot.id}
        onClose={() => {
          setMemoModalOpen(false);
        }}
        initialTags={currentTags}
        initialRemarks={currentRemarks}
        initialAdditionalFields={currentAdditionalFields}
        onTagsSave={handleSaveTags}
        onRemarksSave={handleSaveRemarks}
        onAdditionalFieldsSave={handleSaveAdditionalFields}
        onOpenFilesPage={handleOpenFilesPage}
        availableFields={availableFields}
      />
      <OtherButtonModal
        open={otherButtonModalOpen}
        onClose={() => {
          setOtherButtonModalOpen(false);
        }}
        kaokuNumberSearchFlag={kaokuNumberSearchFlag}
        handleKaokuNumberSearch={handleClickKaokuNumberSearchButton}
        realEstateDocumentFetchFlag={realEstateDocumentFetchFlag}
        handleRealEstateDocumentFetchModal={handleRealEstateDocumentFetch}
        handleRealEstateResearchRequestModal={handleRealEstateResearchRequest}
        propertySummary={propertySummary}
        handleNewPropertySheet={handleCreatePropertySheet}
        googleMapsUrl={googleMapsUrl}
        chikaMapUrl={priceMap ? chikaMapUrl : undefined}
      />
    </>
  );
};
